import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import api from '@/services/api'

// Path prefixes that are NOT public website views — never tracked.
const EXCLUDED_PREFIXES = ['/admin', '/account', '/portal', '/reseller-portal']

/**
 * Fires a cookie-based page-view track on initial mount and on every pathname
 * change. Tracking is best-effort: failures are swallowed silently so they can
 * never break the app. Internal areas (admin/account/portal/reseller-portal)
 * are skipped, and a guard prevents duplicate fires for the same path in quick
 * succession (e.g. double-render / replace navigations).
 *
 * The `api` axios instance shares the site origin, so the visitor cookie set by
 * the backend round-trips automatically.
 */
export function usePageTracking() {
  const location = useLocation()
  // Last path we successfully fired for — guards against duplicate sends.
  const lastTrackedRef = useRef<string | null>(null)

  useEffect(() => {
    const path = location.pathname

    // Skip internal (non-public) areas.
    if (EXCLUDED_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`))) {
      return
    }

    // Debounce/guard duplicate fires for the same path.
    if (lastTrackedRef.current === path) {
      return
    }
    lastTrackedRef.current = path

    try {
      api
        .post('/track/pageview', {
          path,
          referrer: document.referrer || undefined,
        })
        .catch(() => {
          // Silent — tracking failures must never surface to the user.
        })
    } catch {
      // Silent — guard against any synchronous throw.
    }
  }, [location.pathname])
}
